import React, { useRef } from "react";
import {
  Lock,
  Gift,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { CheckoutGiftDto } from "../../../types/Response/CustomerResponse";

interface Props {
  totalPrice: number;
  claimedRewardIds: number[];
  onClaim: (rewardProduct: any) => void;
  availableGifts?: CheckoutGiftDto[];
  showCelebration?: boolean;
}

const FreeClaimComponent: React.FC<Props> = ({
  claimedRewardIds,
  onClaim,
  availableGifts = [],
  showCelebration = false,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 180;

      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div
      className="
        w-full
        bg-white
        rounded-xl
        border border-slate-200/80
        p-4
        max-[639px]:p-2.5
        shadow-sm
        text-left
        relative
        overflow-hidden
        group
      "
    >
      {/* =========================================================
          CELEBRATION OVERLAY
      ========================================================= */}
      {showCelebration && (
        <div
          className="
            absolute inset-0
            bg-[#2F5E2E]/95
            backdrop-blur-xs
            z-30
            flex flex-col
            items-center
            justify-center
            text-white
            animate-in
            fade-in
            zoom-in-95
            duration-300
            p-4
          "
        >
          <div className="flex items-center gap-2 animate-bounce">
            <Sparkles className="text-amber-300 w-5 h-5 fill-amber-300" />

            <h3 className="text-sm font-black tracking-wider uppercase">
              Yay! Free Gift Unlocked!
            </h3>

            <Sparkles className="text-amber-300 w-5 h-5 fill-amber-300" />
          </div>

          <p className="text-[11px] text-emerald-100 font-medium mt-1 text-center">
            Added to your checkout order summary layout maps cleanly.
          </p>
        </div>
      )}

      {/* =========================================================
          HEADER
      ========================================================= */}
      <div
        className="
          flex items-center justify-between
          border-b border-slate-100
          pb-2.5
          max-[639px]:pb-2
          mb-3
          max-[639px]:mb-2
        "
      >
        <div className="flex items-center gap-2 max-[639px]:gap-1.5">
          <Gift
            className="
              text-[#2F5E2E]
              w-4.5 h-4.5
              max-[639px]:w-4
              max-[639px]:h-4
              stroke-[2.5]
            "
          />

          <h2
            className="
              text-xs
              max-[639px]:text-[10px]
              font-black
              text-slate-900
              tracking-wide
              uppercase
            "
          >
            Free Gifts
          </h2>
        </div>
      </div>

      {/* =========================================================
          GIFT RAIL
      ========================================================= */}
      <div className="relative w-full">
        {/* LEFT BUTTON */}
        {availableGifts.length > 2 && (
          <button
            type="button"
            onClick={() => handleScroll("left")}
            className="
              absolute
              left-[-10px]
              max-[639px]:left-[-6px]
              top-1/2
              -translate-y-1/2
              z-20
              w-7 h-7
              max-[639px]:w-6
              max-[639px]:h-6
              rounded-full
              border border-slate-200
              bg-white
              hover:bg-slate-50
              text-slate-700
              flex
              items-center
              justify-center
              shadow-md
              transition
              cursor-pointer
              active:scale-95
            "
          >
            <ChevronLeft
              size={15}
              className="max-[639px]:w-3 max-[639px]:h-3 stroke-[2.5]"
            />
          </button>
        )}

        {/* RIGHT BUTTON */}
        {availableGifts.length > 2 && (
          <button
            type="button"
            onClick={() => handleScroll("right")}
            className="
              absolute
              right-[-10px]
              max-[639px]:right-[-6px]
              top-1/2
              -translate-y-1/2
              z-20
              w-7 h-7
              max-[639px]:w-6
              max-[639px]:h-6
              rounded-full
              border border-slate-200
              bg-white
              hover:bg-slate-50
              text-slate-700
              flex
              items-center
              justify-center
              shadow-md
              transition
              cursor-pointer
              active:scale-95
            "
          >
            <ChevronRight
              size={15}
              className="max-[639px]:w-3 max-[639px]:h-3 stroke-[2.5]"
            />
          </button>
        )}

        {/* =========================================================
            EMPTY STATE
        ========================================================= */}
        {availableGifts.length === 0 ? (
          <p
            className="
              text-[11px]
              max-[639px]:text-[10px]
              text-slate-400
              italic
              text-center
              py-3
              max-[639px]:py-2
            "
          >
            No promotional rewards active for this checkout window profile.
          </p>
        ) : (
          <div
            ref={scrollContainerRef}
            className="
              flex
              items-center
              gap-3
              max-[639px]:gap-1.5
              overflow-x-auto
              pb-1
              scroll-smooth
              scrollbar-none
              snap-x
              snap-mandatory
              px-1
            "
          >
            {availableGifts.map((gift: any, idx) => {
              const isUnlocked = gift.isUnlocked;
              const isSelectedGift = gift.isSelected;

              const isClaimed =
                claimedRewardIds.includes(gift.productVariantId) ||
                isSelectedGift;

              return (
                <div
                  key={gift.productVariantId || idx}
                  className="
                    flex
                    items-center
                    shrink-0
                    snap-start
                  "
                >
                  {/* =================================================
                      GIFT CARD
                  ================================================= */}
                  <div
                    className={`
                      relative
                      w-36
                      h-[190px]

                      max-[639px]:w-28
                      max-[639px]:h-[155px]

                      rounded-lg
                      border

                      p-2
                      max-[639px]:p-1.5

                      flex
                      flex-col
                      items-center
                      justify-between

                      bg-white
                      transition-all
                      duration-300

                      ${
                        isSelectedGift
                          ? "border-emerald-600 bg-emerald-50/40 ring-2 ring-emerald-600/20 shadow-sm"
                          : isClaimed
                          ? "border-emerald-500 bg-emerald-50/20"
                          : isUnlocked
                          ? "border-[#6DBE45] shadow-xs"
                          : "border-slate-100 opacity-60"
                      }
                    `}
                  >
                    {/* =================================================
                        IMAGE
                    ================================================= */}
                    <div
                      className="
                        w-full
                        h-20
                        max-[639px]:h-14

                        bg-slate-50
                        rounded-md
                        overflow-hidden
                        relative
                        border border-slate-100

                        flex
                        items-center
                        justify-center

                        p-1.5
                        max-[639px]:p-1

                        shrink-0
                      "
                    >
                      <img
                        src={gift.imageUrl || "/placeholder-gift.png"}
                        alt={gift.productName || "Reward"}
                        className="max-w-full max-h-full object-contain"
                      />

                      {!isUnlocked && (
                        <div
                          className="
                            absolute inset-0
                            bg-slate-900/40
                            backdrop-blur-3xs
                            flex
                            items-center
                            justify-center
                            text-white
                          "
                        >
                          <Lock
                            size={15}
                            className="max-[639px]:w-3 max-[639px]:h-3"
                          />
                        </div>
                      )}
                    </div>

                    {/* =================================================
                        DETAILS
                    ================================================= */}
                    <div
                      className="
                        flex
                        flex-col
                        items-center
                        justify-between
                        gap-1
                        max-[639px]:gap-0.5

                        w-full
                        min-w-0
                        flex-1

                        mt-1
                        max-[639px]:mt-0.5
                      "
                    >
                      <h4
                        className="
                          text-[11px]
                          max-[639px]:text-[9px]

                          font-bold
                          text-slate-800
                          text-center
                          truncate
                          w-full
                          block
                          leading-snug
                        "
                      >
                        {gift.productName || "Mystery Reward"}
                      </h4>

                      <div
                        className="
                          w-full
                          mt-auto
                        "
                      >
                        {/* =================================================
                            SELECTED
                        ================================================= */}
                        {isSelectedGift ? (
                          <span
                            className="
                              text-[9px]
                              max-[639px]:text-[7px]

                              font-black
                              text-emerald-800
                              bg-emerald-100/80

                              py-1.5
                              max-[639px]:py-1

                              rounded-md

                              flex
                              items-center
                              justify-center
                              gap-1

                              uppercase
                              tracking-wide

                              border border-emerald-200/60
                              animate-pulse
                            "
                          >
                            <CheckCircle
                              size={10}
                              className="max-[639px]:w-2.5 max-[639px]:h-2.5 stroke-[3]"
                            />

                            Selected
                          </span>
                        ) : isClaimed ? (
                          /* CLAIMED */
                          <span
                            className="
                              text-[10px]
                              max-[639px]:text-[8px]

                              font-black
                              text-emerald-700
                              bg-emerald-50
                              border border-emerald-100

                              py-1.5
                              max-[639px]:py-1

                              rounded-md
                              block
                              text-center

                              uppercase
                              tracking-wide
                            "
                          >
                            Claimed
                          </span>
                        ) : isUnlocked ? (
                          /* CLAIM */
                          <button
                            type="button"
                            onClick={() => onClaim(gift)}
                            className="
                              w-full

                              py-1.5
                              max-[639px]:py-1

                              bg-[#2F5E2E]
                              hover:bg-[#1f3f1e]

                              text-white

                              text-[10px]
                              max-[639px]:text-[8px]

                              font-black
                              rounded-md

                              transition
                              shadow-xs
                              cursor-pointer

                              uppercase
                              tracking-wider
                              text-center
                            "
                          >
                            Claim
                          </button>
                        ) : (
                          /* LOCKED */
                          <span
                            className="
                              text-[9px]
                              max-[639px]:text-[7px]

                              font-bold
                              text-rose-500

                              block
                              text-center

                              font-sans
                              leading-tight

                              line-clamp-2

                              px-0.5
                            "
                          >
                            {gift.unlockMessage ||
                              `Spend ${gift.cartAmountThreshold} AED more to unlock`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                      CONNECTING LINE
                  ================================================= */}
                  {idx !== availableGifts.length - 1 && (
                    <div
                      className={`
                        w-4
                        max-[639px]:w-2

                        h-0.5
                        shrink-0

                        ${
                          isUnlocked
                            ? "bg-[#6DBE45]"
                            : "bg-slate-200"
                        }
                      `}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FreeClaimComponent;